import React, { useState, useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { Box, Typography, Link, Alert } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

interface FileUploadComponentProps {
  onUploadFile: (file: File) => void;
}

const FileUploadComponent: React.FC<FileUploadComponentProps> = ({ onUploadFile }) => {
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      // Reset error state
      setError(null);

      // Check for file rejections (e.g., size exceeds max limit)
      if (fileRejections.length > 0) {
        const rejection = fileRejections[0];
        if (rejection.errors.some((err) => err.code === 'file-too-large')) {
          setError('The file exceeds the maximum size of 5MB.');
          return;
        }
      }

      // Handle file upload if valid
      if (acceptedFiles.length > 0) {
        console.log(acceptedFiles);
        onUploadFile(acceptedFiles[0]);
      }
    },
    [onUploadFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxSize: 5242880, // 5MB limit
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '1px dashed',
        borderColor: error ? 'red' : 'grey', // Change border color to red if there's an error
        borderRadius: 1,
        padding: 3,
        textAlign: 'center',
        margin: 'auto',
        cursor: 'pointer',
        backgroundColor: isDragActive ? '#f0f0f0' : 'inherit',
      }}
      data-testid="file-upload-box"
    >
      <input {...getInputProps()} data-testid="file-upload-input" />
      <UploadFileIcon
        sx={{
          fontSize: 40,
          color: isDragActive ? 'primary.main' : 'text.secondary',
        }}
      />
      <Typography variant="body1" color="textSecondary">
        <Link href="#" onClick={(e) => e.preventDefault()}>
          Click here
        </Link>{' '}
        or drag and drop
      </Typography>
      <Typography variant="body2" color="textSecondary">
        CSV (max. 5MB)
      </Typography>
      {error && (
        <Alert severity="error" sx={{ marginTop: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default FileUploadComponent;
